// ============================================================
// mobileNumber.ts — B4c (D4): the UK MOBILE number an e-signature code is
// texted to (client / litigation friend / witness). The SHARED block below is
// identical to the server's (backend/src/lib/mobileNumber.ts — the parity test
// backend/test/mobileNumberParity.test.ts checks it):
//   • normaliseUkMobile — 07xxx xxxxxx / +44 7xxx… / 44 7xxx… / 0044 7xxx…, with
//     spaces, dashes, dots or brackets; a UK mobile (07 then 9 digits) → E.164
//     (+447700900123). Blank → null (clears).
//   • formatUkMobile — "07700 900123" for display.
//   • maskMobile — "07•••••123".
// ============================================================

// ── SHARED (identical in src/utils/mobileNumber.ts) ──
export const MOBILE_FORMAT_ERROR = 'Enter a UK mobile number, like 07700 900123.'
export type MobileNormalised = { ok: true; e164: string | null } | { ok: false; error: string }

export const normaliseUkMobile = (input: unknown): MobileNormalised => {
  if (input === null || input === undefined) return { ok: true, e164: null }
  if (typeof input !== 'string') return { ok: false, error: MOBILE_FORMAT_ERROR }
  const raw = input.trim()
  if (!raw) return { ok: true, e164: null }
  if (raw.length > 30 || !/^[+0-9\s().-]+$/.test(raw)) return { ok: false, error: MOBILE_FORMAT_ERROR }
  const plus = raw.startsWith('+')
  if (raw.slice(1).includes('+')) return { ok: false, error: MOBILE_FORMAT_ERROR }
  let digits = raw.replace(/\D/g, '')
  let national: string
  if (plus || digits.startsWith('0044') || (digits.startsWith('44') && digits.length >= 12)) {
    if (digits.startsWith('00')) digits = digits.slice(2)
    if (!digits.startsWith('44')) return { ok: false, error: MOBILE_FORMAT_ERROR }
    national = digits.slice(2)
    // "+44 (0) 7700 900123" — the trunk 0 written after the country code.
    if (national.startsWith('0')) national = national.slice(1)
  } else if (digits.startsWith('0')) {
    national = digits.slice(1)
  } else {
    return { ok: false, error: MOBILE_FORMAT_ERROR }
  }
  if (!/^7\d{9}$/.test(national)) return { ok: false, error: MOBILE_FORMAT_ERROR }
  return { ok: true, e164: `+44${national}` }
}

const nationalOf = (e164: string): string | null => (/^\+447\d{9}$/.test(e164) ? `0${e164.slice(3)}` : null)

// "07700 900123". Anything that isn't a stored UK mobile comes back as it is.
export const formatUkMobile = (e164: string | null | undefined): string => {
  if (!e164) return ''
  const n = nationalOf(e164)
  return n ? `${n.slice(0, 5)} ${n.slice(5)}` : e164
}

// "07•••••123" — never the whole number. Null / blank → null.
export const maskMobile = (e164: string | null | undefined): string | null => {
  if (!e164) return null
  const n = nationalOf(e164) ?? e164.replace(/\D/g, '')
  if (n.length < 5) return '•••••'
  return `${n.slice(0, 2)}•••••${n.slice(-3)}`
}
// ── END SHARED ──
